import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { DEFAULT_LANG } from './index';
import { translate } from './translate';
import { storage } from '../services/storage';
import { getDeviceLanguage, normalizeDisplayLanguage } from '../utils/locale';

type I18nValue = {
  lang: string; // seçili görüntüleme dili (ör. 'tr', 'en', 'de')
  t: (key: string, params?: Record<string, string | number>) => string;
  setLang: (code: string) => void;
};

const I18nContext = createContext<I18nValue>({
  lang: DEFAULT_LANG,
  t: (k) => k,
  setLang: () => {},
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<string>(getDeviceLanguage());

  useEffect(() => {
    storage.getDisplayLanguage().then((saved) => {
      if (!saved) return;
      const normalized = normalizeDisplayLanguage(saved);
      setLangState(normalized);
      // Eski sürümlerin `pt`, `zh-CN` veya artık desteklenmeyen kodlarını bir kez
      // düzelt; böylece arayüz ve token kaydındaki bildirim dili ayrışmaz.
      if (normalized !== saved) storage.setDisplayLanguage(normalized).catch(() => {});
    });
  }, []);

  const setLang = useCallback((code: string) => {
    const normalized = normalizeDisplayLanguage(code);
    const previous = lang;
    setLangState(normalized);
    storage.setDisplayLanguage(normalized).catch(() => {
      setLangState((current) => current === normalized ? previous : current);
    });
  }, [lang]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      return translate(lang, key, params);
    },
    [lang]
  );

  return <I18nContext.Provider value={{ lang, t, setLang }}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  return useContext(I18nContext);
}
